import { useEffect } from "react";

import { Plug } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { trackEvent } from "@needl-ai/common/analytics";
import { Skeleton } from "@needl-ai/common/components/ui/skeleton";
import { toast } from "@needl-ai/common/components/ui/sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@needl-ai/common/components/ui/tooltip";
import { useIntegrationSources } from "@needl-ai/common/integrations/hooks/useIntegrationSources";
import { useGetPathPrefix } from "@needl-ai/common/workspace";

interface IntegrationsButtonProps {
  onNavigateToIntegrations?: () => void;
  isStreaming?: boolean;
  isLoading?: boolean;
  hasIncompleteAttachments?: boolean;
  isCollapsed?: boolean;
}

const IntegrationsButton = ({
  isStreaming = false,
  isLoading = false,
  hasIncompleteAttachments = false,
  isCollapsed = false,
}: IntegrationsButtonProps) => {
  const navigate = useNavigate();
  const pathPrefix = useGetPathPrefix();
  const {
    data: integrationSources = [],
    isLoading: isLoadingSources,
    isError,
  } = useIntegrationSources();

  useEffect(() => {
    if (isError) {
      toast.error("Failed to load integrations", {
        description: "There was an error loading integration sources.",
      });
    }
  }, [isError]);

  if (isLoadingSources) {
    return (
      <div
        className={
          isCollapsed
            ? "w-12 h-12 rounded-lg flex items-center justify-center"
            : "w-full h-12 rounded-lg flex items-center gap-4 px-4"
        }
      >
        <Skeleton className={isCollapsed ? "w-full h-full" : "w-full h-8"} />
      </div>
    );
  }

  if (integrationSources.length === 0) {
    return null;
  }

  const isDisabled = isStreaming || isLoading || hasIncompleteAttachments;

  const buttonClass = !isDisabled
    ? `${
        isCollapsed
          ? "w-12 h-12 rounded-lg flex items-center justify-center"
          : "w-full h-12 rounded-lg flex items-center gap-4 px-4"
      } transition-colors font-normal hover:font-medium active:font-medium text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 active:text-gray-900 dark:text-gray-200 dark:hover:bg-neutral-800 dark:hover:text-white dark:active:text-white`
    : `${
        isCollapsed
          ? "w-12 h-12 rounded-lg flex items-center justify-center"
          : "w-full h-12 rounded-lg flex items-center gap-4 px-4"
      } transition-colors font-normal text-sm opacity-75 cursor-not-allowed text-gray-400`;

  const tooltipText =
    isStreaming || isLoading
      ? "Integrations disabled during processing..."
      : hasIncompleteAttachments
        ? "Complete file uploads before accessing integrations"
        : "Manage integrations";

  const onNavigateToIntegrations = () => {
    trackEvent("integrations_clicked");
    navigate(`${pathPrefix}/integrations`);
  };

  if (isCollapsed) {
    return (
      <div className="px-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={isDisabled ? undefined : onNavigateToIntegrations}
                className={buttonClass}
                disabled={isDisabled}
                aria-label={tooltipText}
                data-testid="integrations-button"
              >
                <Plug
                  size={16}
                  className="hover:text-gray-900 active:text-gray-900 dark:hover:text-white dark:active:text-white"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className="px-4">
      <button
        onClick={isDisabled ? undefined : onNavigateToIntegrations}
        className={buttonClass}
        disabled={isDisabled}
        title={tooltipText}
        data-testid="integrations-button"
      >
        <Plug
          size={16}
          className="hover:text-gray-900 active:text-gray-900 dark:hover:text-white dark:active:text-white"
        />
        {!isCollapsed && "Integrations"}
      </button>
    </div>
  );
};

export default IntegrationsButton;
